// frontend/src/store/settingsStore.ts
import { create } from 'zustand';
import { getPublicGeneralSettings as apiGetPublicGeneralSettings } from '@/api/settingService';
import { type PublicGeneralSettings } from '@/types';

interface SettingsState {
  publicSettings: PublicGeneralSettings | null;
  isLoading: boolean;
  error: string | null;
  fetchSiteSettings: () => Promise<void>;
  getSetting: <K extends keyof PublicGeneralSettings>(
    key: K,
    defaultValue?: PublicGeneralSettings[K]
  ) => PublicGeneralSettings[K] | undefined | null;
}

export const useSiteSettingsStore = create<SettingsState>((set, get) => ({
  publicSettings: null,
  isLoading: false,
  error: null,

  fetchSiteSettings: async () => {
    if (get().isLoading) return;
    set({ isLoading: true, error: null });
    try {
      const fetchedPublicSettings = await apiGetPublicGeneralSettings();
      set({ publicSettings: fetchedPublicSettings, isLoading: false });
    } catch (err) {
      console.error("Failed to fetch public site settings:", err);
      const errorMessage = err instanceof Error ? err.message : "Could not load site configuration.";
      set({ error: errorMessage, isLoading: false, publicSettings: null });
    }
  },

  getSetting: <K extends keyof PublicGeneralSettings>(
    key: K,
    defaultValue?: PublicGeneralSettings[K]
  ): PublicGeneralSettings[K] | undefined | null => {
    const settings = get().publicSettings;
    if (settings && typeof settings === 'object' && key in settings) {
      const value = settings[key];
      return value !== undefined && value !== null ? value : defaultValue;
    }
    return defaultValue;
  },
}));